import React, { useEffect, useState } from 'react';
import { apiGet, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { Search, Trash2, Sparkles, Library as LibraryIcon, Settings } from 'lucide-react';
import { inr } from '../../lib/format';

const SOURCE_BADGES = {
  seed: { label: 'STD', cls: 'bg-blue-50 text-blue-700' },
  ai: { label: 'AI', cls: 'bg-purple-50 text-purple-700' },
  manual: { label: 'MAN', cls: 'bg-slate-100 text-slate-700' },
};

export default function RateLibrary() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search.trim()) params.append('q', search.trim());
    const res = await apiGet(`/rate-library?${params.toString()}`);
    setItems(res.items);
    setCategories(res.categories);
  };
  useEffect(() => { load(); }, [category, search]);

  const del = async (id) => {
    if (!window.confirm('Delete this library item?\n\nThis action cannot be undone.')) return;
    try {
      await apiDel(`/rate-library/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const summary = {
    total: items.length,
    seed: items.filter(i => i.source === 'seed').length,
    ai: items.filter(i => i.source === 'ai').length,
    manual: items.filter(i => i.source === 'manual').length,
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="rate-library-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Bid & Estimation</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Rate Library</h1>
        <p className="text-sm text-slate-600 mt-1">Master rate analysis for common construction items. AI auto-learns and adds new items here for future use.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <Stat icon={LibraryIcon} label="Total Items" value={summary.total} color="text-slate-700" />
        <Stat icon={Settings} label="Standard" value={summary.seed} color="text-blue-600" />
        <Stat icon={Sparkles} label="AI-Learned" value={summary.ai} color="text-purple-600" />
        <Stat icon={LibraryIcon} label="Manual" value={summary.manual} color="text-slate-600" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by keyword (e.g., concrete m25, tile)…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="library-search-input"
          />
        </div>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded text-sm"
          data-testid="library-category-filter"
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Item</th>
              <th className="text-left px-4 py-2 font-semibold">Category</th>
              <th className="text-left px-4 py-2 font-semibold w-16">Unit</th>
              <th className="text-right px-4 py-2 font-semibold w-28">Base Rate</th>
              <th className="text-center px-4 py-2 font-semibold w-20">Source</th>
              <th className="px-4 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10">No items found.</td></tr>
            ) : items.map(it => {
              const badge = SOURCE_BADGES[it.source] || SOURCE_BADGES.manual;
              return (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(it)} data-testid={`library-row-${it.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-800">{it.item_name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-md">
                      {(it.keywords || []).slice(0, 5).join(' · ')}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{it.category}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{it.unit}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{inr(it.base_rate)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <button onClick={e => { e.stopPropagation(); del(it.id); }} className="text-slate-400 hover:text-red-600" data-testid={`delete-library-${it.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && <DetailModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 p-4 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <Icon size={16} className={color} />
      </div>
      <div className="text-2xl font-display font-extrabold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function DetailModal({ item, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div className="text-xs uppercase tracking-wide text-slate-500">{item.category}</div>
          <h2 className="font-display text-lg font-bold mt-1">{item.item_name}</h2>
          <div className="text-xs text-slate-500 mt-1">Per {item.unit} · Base Rate ₹{item.base_rate?.toFixed(2)} · Source: {item.source?.toUpperCase()}</div>
        </div>
        <div className="p-5 space-y-4">
          {['materials', 'labour', 'equipment'].map(key => (
            <div key={key}>
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-600 mb-1">{key}</div>
              {(item[key] || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">None</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-2 py-1">Name</th>
                      <th className="text-right px-2 py-1">Qty/Hrs</th>
                      <th className="text-left px-2 py-1">Unit</th>
                      <th className="text-right px-2 py-1">Rate</th>
                      <th className="text-right px-2 py-1">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item[key].map((c, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-2 py-1">{c.name || c.role}</td>
                        <td className="px-2 py-1 text-right">{c.qty_per_unit ?? c.hours_per_unit}</td>
                        <td className="px-2 py-1">{c.unit || 'Hr'}</td>
                        <td className="px-2 py-1 text-right">₹{c.rate}</td>
                        <td className="px-2 py-1 text-right font-medium">₹{c.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          {item.keywords?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-600 mb-1">Keywords (for matching)</div>
              <div className="flex flex-wrap gap-1">
                {item.keywords.map(k => <span key={k} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{k}</span>)}
              </div>
            </div>
          )}
          {item.notes && <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-700">{item.notes}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-white">Close</button>
        </div>
      </div>
    </div>
  );
}
